import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { auth } from '@/firebase';
import { onAuthStateChanged, getIdTokenResult } from 'firebase/auth';
import type { UserRole } from '@/services/auth';
import { getUserRole, restGetUserMapping } from '@/services/firestore';

interface ProtectedRouteProps {
  allow: UserRole[]; // roles permitidas
  children: ReactNode;
}

export function ProtectedRoute({ allow, children }: ProtectedRouteProps) {
  const { toast } = useToast();
  const [status, setStatus] = useState<'loading' | 'allowed' | 'denied' | 'redirect'>('loading');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setStatus('redirect');
        return;
      }
      try {
        const token = await getIdTokenResult(user);
        const claimsRole = token.claims?.role as UserRole;
        let role = claimsRole || (await getUserRole(user.uid));
        if (!role) {
          const rest = await restGetUserMapping(user.uid);
          role = rest?.role;
        }
        if (!role) {
          const localRole = (localStorage.getItem('userType') || '') as UserRole;
          if (localRole && allow.includes(localRole)) {
            setStatus('allowed');
            return;
          }
          toast({ title: 'Acesso restrito', description: 'Seu perfil não possui permissão para esta área.', variant: 'destructive' });
          setStatus('denied');
          return;
        }
        if (allow.includes(role)) {
          setStatus('allowed');
        } else {
          toast({
            title: 'Acesso negado',
            description: 'Você não possui permissão para esta área.',
            variant: 'destructive',
          });
          setStatus('denied');
        }
      } catch {
        setStatus('denied');
      }
    });
    return () => unsub();
  }, [allow, toast]);

  if (status === 'loading') {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregando…</div>;
  }
  if (status === 'redirect') {
    return <Navigate to="/login" replace />;
  }
  if (status === 'denied') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export default ProtectedRoute;
